import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import {
  searchUsers,
  addCohost,
  removeCohost,
  resetCohostState,
} from "../redux/slices/cohostSlice";
import { fetchSpecificEvent } from "../redux/slices/userSlice";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 600px;
  max-width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #060b13;
  }

  .close-icon {
    cursor: pointer;
    color: #828286;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
  position: relative;

  input {
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid #bac0d4b2;
    font-size: 14px;
    outline: none;

    &:focus {
      border-color: #ed1c24;
    }
  }

  .search-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #828286;
  }
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
`;

const UserItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;

  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;

    img {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }

    div {
      display: flex;
      flex-direction: column;

      span.name {
        font-weight: 500;
        color: #060b13;
      }

      span.email {
        font-size: 12px;
        color: #828286;
      }
    }
  }

  button {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: background 0.2s;

    &.add-btn {
      background: #ed1c24;
      color: white;

      &:hover {
        background: #c4161c;
      }
    }

    &.remove-btn {
      background: #f0f0f0;
      color: #ed1c24;

      &:hover {
        background: #e0e0e0;
      }
    }
  }
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #060b13;
  margin-bottom: 12px;
  margin-top: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #828286;
  padding: 20px;
  font-size: 14px;
`;

const CohostModal = ({ onClose, eventId, currentCohosts = [] }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const { searchResults, loading, addCohostSuccess, removeCohostSuccess } =
    useSelector((state) => state.cohosts);

  // Debounce search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.trim()) {
        dispatch(searchUsers({ key: searchTerm, page: 1, pageSize: 10 }));
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, dispatch]);

  useEffect(() => {
    if (addCohostSuccess) {
      toast.success("Cohost added successfully");
      dispatch(fetchSpecificEvent(eventId)); // Refresh event data to update cohost list
      dispatch(resetCohostState());
    }
    if (removeCohostSuccess) {
      toast.success("Cohost removed successfully");
      dispatch(fetchSpecificEvent(eventId)); // Refresh event data
      dispatch(resetCohostState());
    }
  }, [addCohostSuccess, removeCohostSuccess, dispatch, eventId]);

  const handleAddCohost = (userId) => {
    dispatch(addCohost({ eventId, userIds: [userId] }));
  };

  const handleRemoveCohost = (userId) => {
    dispatch(removeCohost({ eventId, userIds: [userId] }));
  };

  const isCohost = (userId) => {
    return currentCohosts.some(
      (cohost) => cohost.id === userId || cohost._id === userId
    );
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>Manage Cohosts</h3>
          <Icon
            icon="mdi:close"
            width="24"
            height="24"
            className="close-icon"
            onClick={onClose}
          />
        </ModalHeader>

        <SearchContainer>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Icon
            icon="mdi:magnify"
            width="20"
            height="20"
            className="search-icon"
          />
        </SearchContainer>

        {searchTerm && (
          <>
            <SectionTitle>Search Results</SectionTitle>
            <UserList>
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "20px",
                  }}
                >
                  <ClipLoader color="#ed1c24" size={24} />
                </div>
              ) : searchResults?.length > 0 ? (
                searchResults.map((user) => (
                  <UserItem key={user.id || user._id}>
                    <div className="user-info">
                      <img
                        src={user.profileImage || "/images/image_1.png"} // Fallback image
                        alt={user.firstName}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/image_1.png";
                        }}
                      />
                      <div>
                        <span className="name">
                          {user.fname} {user.lname}
                        </span>
                        <span className="email">{user.email}</span>
                      </div>
                    </div>
                    {!isCohost(user.id || user._id) ? (
                      <button
                        className="add-btn"
                        onClick={() => handleAddCohost(user.id || user._id)}
                      >
                        Add
                      </button>
                    ) : (
                      <span style={{ fontSize: "12px", color: "#828286" }}>
                        Already Added
                      </span>
                    )}
                  </UserItem>
                ))
              ) : (
                <EmptyState>No users found</EmptyState>
              )}
            </UserList>
          </>
        )}

        <SectionTitle>Current Cohosts</SectionTitle>
        <UserList>
          {currentCohosts.length > 0 ? (
            currentCohosts.map((cohost) => (
              <UserItem key={cohost.id || cohost._id}>
                <div className="user-info">
                  <img
                    src={cohost.profileImage || "/images/image_1.png"}
                    alt={cohost.firstName}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/images/image_1.png";
                    }}
                  />
                  <div>
                    <span className="name">
                      {cohost.firstName} {cohost.lastName}
                    </span>
                    <span className="email">{cohost.email}</span>
                  </div>
                </div>
                <button
                  className="remove-btn"
                  onClick={() => handleRemoveCohost(cohost.id || cohost._id)}
                >
                  Remove
                </button>
              </UserItem>
            ))
          ) : (
            <EmptyState>No cohosts added yet</EmptyState>
          )}
        </UserList>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default CohostModal;
